/**
 * Security Utilities for QC ID Data Handling
 * Provides sanitization, validation, and secure logging functions
 * 
 * [OWASP Mappings]:
 * - A03: Injection (XSS Prevention via DOMPurify)
 * - A04: Insecure Design (Input/Output Validation)
 * - A05: Injection (Response Validation)
 * - A09: Logging & Monitoring (No PII Logging)
 */

import DOMPurify from 'dompurify';

/**
 * QC ID format validation regex: 14-16 digits only
 * Prevents injection attacks via QC ID field
 * [OWASP:A03] Input validation against injection
 */
const QCID_FORMAT_REGEX = /^\d{14,16}$/;

/**
 * Sanitizes HTML strings to prevent XSS attacks
 * Uses DOMPurify to remove script tags, event handlers, etc.
 * 
 * [OWASP:A03] XSS Prevention
 * 
 * @param {string} value - The string to sanitize
 * @returns {string} - Sanitized string safe for display
 */
export const sanitizeHtmlString = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  
  // Configure DOMPurify to be strict
  const config = {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  };
  
  return DOMPurify.sanitize(value, config).trim();
};

/**
 * Deep sanitizes an object's string values
 * Recursively sanitizes all string properties
 * 
 * [OWASP:A03] XSS Prevention for complex objects
 * 
 * @param {object} obj - The object to sanitize
 * @returns {object} - Object with all string values sanitized
 */
export const sanitizeResponseObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const sanitized = Array.isArray(obj) ? [...obj] : { ...obj };
  
  for (const key in sanitized) {
    if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
      const value = sanitized[key];
      
      if (typeof value === 'string') {
        sanitized[key] = sanitizeHtmlString(value);
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitizeResponseObject(value);
      }
    }
  }
  
  return sanitized;
};

/**
 * Validates QC ID format (14-16 digits only)
 * Prevents injection attacks and invalid lookups
 * 
 * [OWASP:A03] Input validation against injection
 * 
 * @param {string} qcID - The QC ID to validate
 * @returns {boolean} - True if valid format, false otherwise
 */
export const validateQcIdFormat = (qcID) => {
  if (!qcID || typeof qcID !== 'string') {
    return false;
  }
  
  return QCID_FORMAT_REGEX.test(qcID);
};

/**
 * Validates date format (YYYY-MM-DD)
 * Ensures dates are in expected format before form population
 * 
 * [OWASP:A04] Input validation
 * 
 * @param {string} date - The date string to validate
 * @returns {boolean} - True if valid YYYY-MM-DD format, false otherwise
 */
export const validateDateFormat = (date) => {
  if (!date || typeof date !== 'string') {
    return false;
  }
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    return false;
  }
  
  // Validate it's an actual valid date
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  
  return dateObj.getFullYear() === year &&
         dateObj.getMonth() === month - 1 &&
         dateObj.getDate() === day;
};

/**
 * Validates entire QC ID API response structure
 * Ensures response has expected fields and format
 * 
 * [OWASP:A05] Output validation and response integrity
 * 
 * @param {object} response - The API response object to validate
 * @returns {object} - { valid: boolean, errors: string[] }
 */
export const validateQcidResponse = (response) => {
  const errors = [];
  
  if (!response || typeof response !== 'object') {
    errors.push('Response is not an object');
    return { valid: false, errors };
  }
  
  // Validate response structure
  if (!response.result) {
    errors.push('Missing result field');
  }
  
  if (response.result && response.result.status_code !== 'GC0') {
    errors.push(`Invalid status code: ${response.result.status_code}`);
  }
  
  const result = response.result || {};
  
  // Validate key fields exist and are strings
  const requiredStringFields = [
    'first_name',
    'middle_name',
    'last_name',
    'birthday',
    'gender_name',
    'Barangay',
    'City'
  ];
  
  requiredStringFields.forEach(field => {
    if (result[field] !== undefined && typeof result[field] !== 'string') {
      errors.push(`Field '${field}' must be a string`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Maps error codes to user-friendly messages (no technical details)
 * Prevents information leakage about system internals
 * 
 * [OWASP:A09] Secure error handling - no sensitive info to client
 * 
 * @param {string|Error} error - The error code or Error object
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (error instanceof Error) {
    const errorName = error.name;
    
    if (errorName === 'AbortError') {
      return 'Request took too long. Please check your connection and try again.';
    }
    
    if (errorName === 'TypeError') {
      return 'Network connection error. Please check your internet and try again.';
    }
  }
  
  const errorCode = typeof error === 'string' ? error : error?.name || 'UNKNOWN';
  
  const errorMap = {
    'TIMEOUT': 'Request took too long. Please try again.',
    'NOT_FOUND': 'QC ID not found. Please check the number and try again.',
    'SERVICE_ERROR': 'Service temporarily unavailable. Please try again later.',
    'NETWORK': 'Network error. Check your internet connection.',
    'INVALID': 'Invalid QC ID format. Must be 14-16 digits.',
    'INVALID_DATA': 'Received unexpected data format. Please try again.',
    'UNAUTHORIZED': 'You do not have permission to access this resource.',
    'RATE_LIMIT': 'Too many requests. Please wait a moment and try again.',
    'UNKNOWN': 'An error occurred. Please try again.',
  };
  
  return errorMap[errorCode] || errorMap['UNKNOWN'];
};

/**
 * Securely logs errors without exposing PII
 * Logs only error type and context, never personal information
 * 
 * [OWASP:A09] Secure logging - no PII exposure
 * 
 * @param {Error} error - The error object
 * @param {string} context - Context where error occurred (e.g., 'fetchAndUpdateQcidData')
 * @returns {void}
 */
export const logErrorSecurely = (error, context = 'unknown') => {
  // Build safe error message without PII
  const errorType = error?.name || 'Unknown';
  const errorCode = error?.code || '';
  const statusCode = error?.response?.status || '';
  
  // Safe log message with no PII
  const safeMessage = `[ERROR] ${context}: ${errorType}${statusCode ? ` (${statusCode})` : ''}${errorCode ? ` [${errorCode}]` : ''}`;
  
  // Log to production monitoring service
  try {
    if (process.env.NODE_ENV === 'production') {
      // Send to production logging service (e.g., Sentry, custom endpoint)
      // Example: sendToLoggingService({ level: 'error', message: safeMessage, context });
      // For now, use console.error in production (still safe - no PII)
      console.error(safeMessage);
    } else {
      // Development logging
      console.warn(safeMessage);
    }
  } catch (logError) {
    // Silently fail if logging fails
  }
};

/**
 * Formats and sanitizes API response data for form population
 * Combines validation and sanitization
 * 
 * [OWASP:A03, A04, A05] Input validation and XSS prevention
 * 
 * @param {object} apiResponse - Raw API response
 * @returns {object} - { success: boolean, data: object|null, error: string|null }
 */
export const processQcidApiResponse = (apiResponse) => {
  try {
    // Validate response structure
    const validation = validateQcidResponse(apiResponse);
    if (!validation.valid) {
      return {
        success: false,
        data: null,
        error: validation.errors[0] || 'Invalid response format'
      };
    }
    
    const result = apiResponse.result;
    
    // Sanitize all string fields
    const sanitizedData = {
      first_name: sanitizeHtmlString(result.first_name),
      middle_name: sanitizeHtmlString(result.middle_name),
      last_name: sanitizeHtmlString(result.last_name),
      suffix_name: sanitizeHtmlString(result.suffix_name),
      birthday: sanitizeHtmlString(result.birthday),
      gender_name: sanitizeHtmlString(result.gender_name),
      civil_status_name: sanitizeHtmlString(result.civil_status_name),
      house_no: sanitizeHtmlString(result.house_no),
      street: sanitizeHtmlString(result.street),
      Barangay: sanitizeHtmlString(result.Barangay),
      City: sanitizeHtmlString(result.City),
      email: sanitizeHtmlString(result.email),
      mobile_no: sanitizeHtmlString(result.mobile_no),
    };
    
    return {
      success: true,
      data: sanitizedData,
      error: null
    };
  } catch (error) {
    logErrorSecurely(error, 'processQcidApiResponse');
    return {
      success: false,
      data: null,
      error: 'Failed to process API response'
    };
  }
};

export default {
  sanitizeHtmlString,
  sanitizeResponseObject,
  validateQcIdFormat,
  validateDateFormat,
  validateQcidResponse,
  getErrorMessage,
  logErrorSecurely,
  processQcidApiResponse,
};
